/** biome-ignore-all lint/correctness/useHookAtTopLevel: <explanation> */
import React from 'react';
import { Observe } from 'rewire-core';
import { WithStyle, withStyles } from 'rewire-ui';
import classNames from 'classnames';
import ListItem from '@material-ui/core/ListItem';
import Typography from '@material-ui/core/Typography';
import { Theme } from '@material-ui/core/styles';
import { ISelectableEntity } from '../DataTypes';
import { makeStyles } from '../utility/styles';
import EntityListModel from './EntityListModel';

const itemRendererStyles = (theme: Theme) => ({
  name: {
    color: theme.palette.dialogPrimary.main,
    fontWeight: 'bold',
  },
});

const useItemRendererStyles = makeStyles(itemRendererStyles);

export interface IItemRendererProps {
  model: EntityListModel;
  entity: ISelectableEntity;
  classes?: any;
}

export const DefaultItemRenderer = ({ model, entity, classes }: IItemRendererProps) => (
  <Observe
    render={() => {
      classes = useItemRendererStyles({ classes });
      return (
        <Typography variant="body2" className={classes.name} display="inline">
          {model.getEntityDisplayName(entity)}
        </Typography>
      );
    }}
  />
);

const styles = (theme: Theme) => ({
  wrapper: {
    margin: '0px',
    width: '100%',
    outline: 'none',
    flexShrink: 0,
  },
  card: {
    width: '100%',
    height: '100%',
    padding: '2px 0px',
    flexShrink: 0,
    borderBottom: `1px solid ${theme.palette.neutralLight.main}`,
    backgroundColor: theme.palette.neutralLight.light,
    '&:hover': {
      backgroundColor: theme.palette.dialogPrimary.light,
      '& p, span': {
        color: theme.palette.common.white,
      },
      '& svg': {},
    },
  },
  itemContainer: {
    cursor: 'pointer',
    padding: '8px',
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: '100%',
  },
  selected: {
    backgroundColor: `${theme.palette.dialogPrimary.main} !important`,
    '& p, span': {
      color: theme.palette.common.white,
    },
    '& svg': {},
    '&:hover': {
      backgroundColor: `${theme.palette.dialogPrimary.light} !important`,
    },
  },
  notSelected: {},
});

export type EntityListCardViewStyles = ReturnType<typeof styles>;

export interface IEntityListCardViewProps {
  model: EntityListModel;
  entity: ISelectableEntity;
  itemRenderer?: React.FunctionComponent<IItemRendererProps>;
  onClick?: (entity: ISelectableEntity) => () => void;
  classes?: any;
  itemClasses?: any;
}

export type EntityListCardViewProps = WithStyle<EntityListCardViewStyles, IEntityListCardViewProps>;

export class EntityListCardView extends React.Component<EntityListCardViewProps> {
  ref: any;

  constructor(props: EntityListCardViewProps) {
    super(props);
    this.ref = React.createRef();
  }

  handleOnKeyDown = (evt: React.KeyboardEvent<any>) => {
    switch (evt.key) {
      case 'Home':
        this.props.model.moveHome();
        evt.preventDefault();
        this.forceUpdate();
        return;

      case 'End':
        this.props.model.moveEnd();
        evt.preventDefault();
        this.forceUpdate();
        return;

      case 'ArrowDown':
        this.props.model.moveNext();
        evt.preventDefault();
        this.forceUpdate();
        return;

      case 'ArrowUp':
        this.props.model.movePrevious();
        evt.preventDefault();
        this.forceUpdate();
        return;
    }
  };

  handleClick = (entity: ISelectableEntity) => () => {
    if (this.props.onClick) {
      this.props.onClick(entity);
      return;
    }
    this.props.model.select(entity);
  };

  render() {
    const { classes, entity, model, itemRenderer, itemClasses } = this.props;
    const ItemRenderer = itemRenderer || DefaultItemRenderer;

    return (
      <Observe
        render={() => {
          const selected = entity.isSelected;
          if (selected && this.ref?.current) {
            this.ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }

          return (
            <div className={classes.wrapper} ref={this.ref} tabIndex={1} onKeyDown={this.handleOnKeyDown}>
              <ListItem className={classNames(classes.card, selected ? classes.selected : classes.notSelected)} selected={selected}>
                <div className={classes.itemContainer} onClick={this.handleClick(entity)}>
                  <ItemRenderer model={model} entity={entity} classes={itemClasses} />
                </div>
              </ListItem>
            </div>
          );
        }}
      />
    );
  }
}

export default withStyles(styles, EntityListCardView);
